/**
 * WashPros Premium Home Page
 * Uber-style clean, premium design
 * Refactored: sections extracted to /home/ sub-components
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSelector from "@/components/shared/LanguageSelector";
import { 
  Star, Shield, Zap, ChevronRight, 
  Menu, X, Sparkles, Droplets
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggleCompact } from "@/components/shared/ThemeToggle";
import { WashProsLogo } from "@/components/shared/WashProsLogo";
import SloganTicker from "@/components/shared/SloganTicker";
import { VEHICLE_TYPES } from "@/components/shared/PremiumVehicleIcons";
import FeaturedVendorsSection from "@/components/home/FeaturedVendorsSection";
import VideoShowcaseSection from "@/components/home/VideoShowcaseSection";
import { VIPCtaSection, VendorCtaSection } from "@/components/home/HomeCtaSections";
import HomeFooter from "@/components/home/HomeFooter";
import { PUBLIC_BOOKING_ENABLED, PUBLIC_VIP_ENABLED } from "@/config/launchFlags";
import useSeo from "@/hooks/useSeo";
import { PUBLIC_PAGE_SEO } from "@/seo/publicPageSeo";

const TRUST_BADGES = [
  { icon: Star, labelKey: "hero_feature_1", color: "amber" },
  { icon: Shield, labelKey: "hero_feature_2", color: "emerald" },
  { icon: Zap, labelKey: "hero_feature_3", color: "cyan" },
];

const PremiumHomePage = () => {
  useSeo(PUBLIC_PAGE_SEO.home);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    fetchFeaturedVendors();
  }, []);

  const fetchFeaturedVendors = async () => {
    // ── P0 Build 20 fix (2026-06-18, Phase 7g.15.110) ────────────
    // Build 19 crashed at WebView startup with "TypeError: t.map is
    // not a function" because the API response sometimes deserializes
    // to a non-array shape (paginated wrapper, HTML error page parsed
    // by axios, or a {vendors: [...]} envelope from an older env).
    // Normalize defensively so setFeaturedVendors NEVER stores a
    // non-array — the renderer is also hardened separately.
    const _toArray = (data) => {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.vendors)) return data.vendors;
      if (data && Array.isArray(data.data)) return data.data;
      if (data && Array.isArray(data.items)) return data.items;
      return [];
    };
    try {
      const response = await apiClient.get("/vendors/featured?limit=4");
      setFeaturedVendors(_toArray(response.data).slice(0, 4));
    } catch {
      try {
        const fallback = await apiClient.get("/vendors?limit=4");
        setFeaturedVendors(_toArray(fallback.data).slice(0, 4));
      } catch {
        // Featured vendors fallback also failed — non-critical, page
        // still renders.  Hard-set [] so the renderer's Array.isArray
        // guard never inherits a stale non-array from a prior state.
        setFeaturedVendors([]);
      }
    }
  };

  const handleBookNow = () => {
    // Pre-launch: route customers to waitlist instead of live booking
    if (!PUBLIC_BOOKING_ENABLED) {
      navigate("/waitlist");
      return;
    }
    if (selectedVehicle === "luxury") {
      // Luxury/Collectible goes through the concierge flow
      if (user) navigate("/dashboard/luxury-concierge");
      else navigate("/signup?redirect=/dashboard/luxury-concierge");
      return;
    }
    navigate(selectedVehicle ? `/book?vehicle=${selectedVehicle}` : "/book");
  };

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      handleBookNow();
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 overflow-hidden">
      {/* Background Effects — distinct treatments for dark vs light theme.
          Dark mode keeps the ethereal blurred orbs at low opacity;
          light mode uses stronger, more saturated orbs so the hero
          never looks like a flat white page. */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Dark-mode orbs — visible only when html has .dark */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[128px] hidden dark:block" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[128px] hidden dark:block" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[128px] hidden dark:block" />
        {/* Light-mode water-drop orbs — visible by default, hidden in dark */}
        <div className="absolute top-0 left-1/4 w-[700px] h-[700px] bg-cyan-300/45 rounded-full blur-[160px] dark:hidden" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-sky-300/40 rounded-full blur-[160px] dark:hidden" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-200/35 rounded-full blur-[160px] dark:hidden" />
      </div>

      {/* Slogan Ticker Banner — Phase 7g.15.99g (2026-06-10) — replaced the
          static centered banner (which only had `animate-pulse` and was cut
          off on narrower viewports) with the same `<SloganTicker />` used
          across admin/customer/vendor portals. Smoothly scrolls
          WAX AND SHINE HEADQUARTERS™ + THE CARWASH CONCIERGE COMPANY™
          plus service callouts horizontally — no cutoff, infinite loop,
          with sound effects + mute toggle baked in. */}
      <SloganTicker speed="normal" />

      {/* Kreyòl Ayisyen waitlist banner removed entirely per Rick
          directive 2026-06-18 (CBQA-P0-rick-remove). Component file
          is preserved on disk in case it's reinstated later, but it
          is no longer mounted anywhere. */}

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-slate-950/80 dark:bg-slate-950/80 light:bg-white/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <WashProsLogo size="md" testId="public-header-brand" />

            <div className="hidden md:flex items-center gap-3">
              <LanguageSelector variant="compact" />
              <ThemeToggleCompact />
              {/* Phase 7g.15.48 (Rick 2026-05-31): SMS Consent link in
                  public main nav for Twilio A2P 10DLC reviewer
                  discoverability. Quiet, low-emphasis style so it
                  doesn't compete with primary CTAs. */}
              <button
                type="button"
                onClick={() => navigate("/sms-consent")}
                className="text-slate-400 hover:text-cyan-300 text-sm transition-colors hidden lg:inline"
                data-testid="nav-sms-consent"
              >
                SMS Consent
              </button>
              {user ? (
                <>
                  <span className="text-slate-400 text-sm">Welcome, {user.name?.split(' ')[0]}</span>
                  <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-300 hover:text-white" data-testid="nav-dashboard">Dashboard</Button>
                  <Button variant="outline" onClick={logout} className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="nav-logout">Logout</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate("/auth/login")} className="text-slate-300 hover:text-white" data-testid="nav-login">{t('nav_login')}</Button>
                  <Button onClick={handleGetStarted} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium px-6 rounded-full hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300" data-testid="nav-get-started">{t('nav_get_started')}</Button>
                </>
              )}
            </div>

            <div className="md:hidden flex items-center gap-2">
              <ThemeToggleCompact />
              <button className="text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-xl border-t border-white/5 animate-in slide-in-from-top duration-200">
            <div className="px-4 py-4 space-y-3">
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-slate-500 text-xs uppercase tracking-wider">Language</span>
                <LanguageSelector variant="compact" />
              </div>
              {user ? (
                <>
                  <p className="text-slate-400 text-sm px-3">Welcome, {user.name}</p>
                  <button onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-3 text-white hover:bg-white/10 rounded-xl transition-colors">Dashboard</button>
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-3 text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition-colors">Logout</button>
                </>
              ) : (
                <>
                  <button onClick={() => { navigate("/auth/login"); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-3 text-white hover:bg-white/10 rounded-xl transition-colors">Login</button>
                  <Button onClick={() => { handleGetStarted(); setMobileMenuOpen(false); }} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-full">Get Started</Button>
                </>
              )}
              {/* SMS Consent — visible to all users for Twilio reviewer reach */}
              <button
                onClick={() => { navigate("/sms-consent"); setMobileMenuOpen(false); }}
                className="w-full text-left px-3 py-3 text-slate-400 hover:text-cyan-300 text-sm rounded-xl transition-colors"
                data-testid="nav-mobile-sms-consent"
              >
                SMS Consent Policy
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-24">
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className={`space-y-8 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30">
              <Droplets className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-semibold">{t('hero_tagline')}</span>
            </div>
            
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
                {t('hero_title_1')}{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">{t('hero_title_2')}</span>
                {" "}{t('hero_title_3')}
              </h1>
              <p className="text-lg text-amber-400 font-semibold" data-testid="hero-slogan">
                Wax &amp; Shine Headquarters™ for every vehicle you own
              </p>
              {/* Investor-grade positioning line — public, no fundraise language */}
              <p
                className="text-slate-700 dark:text-slate-200 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed"
                data-testid="hero-positioning-line"
              >
                An IP-protected, AI-enabled platform built to modernize mobile wash,
                detailing, fleet care, and vendor operations.
              </p>
              {/* Single instance of the canonical category list — never duplicated inside the slogan paragraph */}
              <div
                className="text-slate-600 dark:text-slate-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed space-y-1"
                data-testid="hero-category-list"
              >
                <p>Cars · Trucks · Boats · Watercraft · RVs</p>
                <p>Fleets · Golf Carts · Recreational Vehicles</p>
                <p>Luxury, Collectibles &amp; Aircraft <span className="text-sky-600 dark:text-sky-300/80">(cosmetic only)</span></p>
              </div>
            </div>

            <div className={`transition-all duration-1000 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Button onClick={handleBookNow} data-testid="hero-book-now" className="h-16 px-12 text-xl font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:scale-105 active:scale-95 transition-all duration-300">
                {PUBLIC_BOOKING_ENABLED ? t('hero_cta') : 'Join the Waitlist'}
                <ChevronRight className="w-6 h-6 ml-2" />
              </Button>
              {!PUBLIC_BOOKING_ENABLED && (
                <p className="text-amber-300/80 text-sm mt-3" data-testid="prelaunch-notice">
                  We're onboarding founding vendors across every Florida county. Customer booking opens soon.
                </p>
              )}
            </div>

            {/* Vehicle Type Selection — Phase 7g.15.88 (Jun 2026):
                4-col × 2-row uniform grid (was 3-col with awkward
                fullWidth aircraft row), labels rendered via t(labelKey)
                instead of the previously undefined `vehicle.label`. */}
            <div className={`transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="grid grid-cols-2 xs:grid-cols-4 gap-2 sm:gap-4 max-w-2xl mx-auto" data-testid="vehicle-types-grid">
                {VEHICLE_TYPES.map((vehicle) => {
                  const Icon = vehicle.icon;
                  const isSelected = selectedVehicle === vehicle.id;
                  const label = t(vehicle.labelKey);
                  return (
                    <button
                      key={vehicle.id}
                      onClick={() => setSelectedVehicle(vehicle.id)}
                      data-testid={`vehicle-type-${vehicle.id}`}
                      aria-label={label}
                      className={`group flex flex-col items-center gap-2 p-2.5 sm:p-4 rounded-2xl transition-all duration-300 ${
                        isSelected
                          ? `bg-gradient-to-br ${vehicle.gradient} shadow-xl shadow-${vehicle.color}-500/30`
                          : 'bg-slate-900/50 hover:bg-slate-800/50 border border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                        isSelected ? 'bg-white/20' : `bg-${vehicle.color}-500/20 group-hover:bg-${vehicle.color}-500/30`
                      }`}>
                        <Icon className={`w-5 h-5 sm:w-7 sm:h-7 ${isSelected ? 'text-white' : `text-${vehicle.color}-400`}`} />
                      </div>
                      <span className={`text-[11px] sm:text-sm font-medium text-center leading-tight px-0.5 break-words ${
                        isSelected ? 'text-white' : 'text-slate-300 group-hover:text-white'
                      }`}>
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Trust Strip */}
            <div className={`transition-all duration-1000 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center justify-center gap-6 sm:gap-12 py-6">
                {TRUST_BADGES.map((badge) => {
                  const Icon = badge.icon;
                  const label = t(badge.labelKey);
                  return (
                    <div key={badge.labelKey} className="flex items-center gap-2 text-slate-400" data-testid={`trust-badge-${badge.labelKey}`}>
                      <Icon className={`w-5 h-5 text-${badge.color}-400`} />
                      <span className="text-sm font-medium hidden sm:inline">{label}</span>
                      <span className="text-xs font-medium sm:hidden">{label.split(' ')[0]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FeaturedVendorsSection vendors={featuredVendors} isLoaded={isLoaded} />
      <VideoShowcaseSection />
      {PUBLIC_VIP_ENABLED && <VIPCtaSection onBookNow={handleBookNow} />}
      <VendorCtaSection />
      <HomeFooter />
    </div>
  );
};

export default PremiumHomePage;
